import React, { useMemo } from "react";
import styled from "styled-components";
import { colors } from "../../../Shared/colors";
import { Skeleton } from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import PhoneCallbackIcon from "@mui/icons-material/PhoneCallback";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ScheduleIcon from "@mui/icons-material/Schedule";

export const TelesalesKPIs = ({ dashboardData, isFetching }) => {
  // Calculate telesales-specific metrics from real API data
  const metrics = useMemo(() => {
    if (!dashboardData || !dashboardData.today) {
      return {
        callsToday: 0,
        callsConnected: 0,
        callsNotConnected: 0,
        conversionRate: 0,
        meetingsScheduled: 0,
        leadsAssigned: 0
      };
    }

    const todayData = dashboardData.today;
    
    return {
      callsToday: todayData.callsToday || 0,
      callsConnected: todayData.callsConnected || 0,
      callsNotConnected: todayData.callsNotConnected || 0,
      conversionRate: todayData.conversionRate || 0,
      meetingsScheduled: todayData.meetingsScheduled || 0,
      leadsAssigned: todayData.leadsAssigned || 0
    };
  }, [dashboardData]);

  const kpiCards = [
    {
      title: "Calls Today",
      value: metrics.callsToday,
      icon: PhoneIcon,
      color: "#3b82f6",
      bgColor: "#dbeafe"
    },
    {
      title: "Calls Connected",
      value: metrics.callsConnected,
      icon: PhoneCallbackIcon,
      color: "#10b981",
      bgColor: "#d1fae5"
    },
    {
      title: "Conversion Rate",
      value: `${metrics.conversionRate}%`,
      icon: TrendingUpIcon,
      color: "#f59e0b",
      bgColor: "#fef3c7"
    },
    {
      title: "Meetings Scheduled",
      value: metrics.meetingsScheduled,
      icon: ScheduleIcon,
      color: "#8b5cf6",
      bgColor: "#ede9fe"
    }
  ];

  return (
    <KPISection>
      <SectionHeader>
        <h2>Today's Performance</h2>
        <p>Real-time metrics for your telesales activities</p>
      </SectionHeader>
      
      <KPIGrid>
        {kpiCards.map((card, index) => (
          <KPICard key={index}>
            <CardHeader>
              <IconContainer bgColor={card.bgColor}>
                <card.icon style={{ color: card.color, fontSize: '1.5rem' }} />
              </IconContainer>
            </CardHeader>
            
            <CardContent>
              <CardTitle>{card.title}</CardTitle>
              <CardValue>
                {isFetching ? (
                  <Skeleton width={60} height={40} />
                ) : (
                  card.value
                )}
              </CardValue>
            </CardContent>
          </KPICard>
        ))}
      </KPIGrid>
    </KPISection>
  );
};

// Styled Components
const KPISection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
  }
  
  @media (max-width: 480px) {
    gap: 1rem;
  }
`;

const SectionHeader = styled.div`
  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${colors.text};
    margin: 0 0 0.5rem 0;
    
    @media (max-width: 768px) {
      font-size: 1.25rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1.1rem;
    }
  }
  
  p {
    color: #64748b;
    margin: 0;
    font-size: 0.95rem;
    
    @media (max-width: 768px) {
      font-size: 0.9rem;
    }
    
    @media (max-width: 480px) {
      font-size: 0.85rem;
    }
  }
`;

const KPIGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.5rem;
  }
`;

const KPICard = styled.div`
  background: ${colors.white};
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    border-radius: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 8px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  
  @media (max-width: 480px) {
    margin-bottom: 0.75rem;
  }
`;

const IconContainer = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 12px;
  background: ${props => props.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    width: 2rem;
    height: 2rem;
    border-radius: 8px;
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CardTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: #64748b;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const CardValue = styled.h2`
  font-size: 2rem;
  font-weight: 800;
  color: ${colors.text};
  margin: 0;
  line-height: 1;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

